import { useEffect, useState } from "react";
import { Alert, Button, FormStack, Input, Modal, Text } from "@marekl/adminer/components";
import { apiErrorMessage } from "@marekl/adminer/api";
import { useT } from "@marekl/adminer/hooks";
import { useCreateLeaderLogin } from "../../../lib/queries/teams";
import type { ID } from "../../../lib/id";

/**
 * CreateLeaderLoginModal gives the team's CURRENT leader an account.
 *
 * „Změnit vedoucího" provisions one only on a handover, so a leader left
 * without an account (seeded, imported) had no way to get one. Leadership
 * does not change here; the team_leader role derives from it and reaches the
 * new account as soon as it is linked to the person.
 *
 * A Modal rather than a FormModal for the same reason as ChangeLeaderModal:
 * once created, the footer collapses to „Zavřít" over the password, which is
 * shown this once and never again.
 */
export function CreateLeaderLoginModal({
  teamId,
  leaderName,
  open,
  onClose,
}: {
  teamId: ID;
  leaderName: string;
  open: boolean;
  onClose: () => void;
}) {
  const t = useT();
  const create = useCreateLeaderLogin(teamId);
  // Stable (React Query memoizes it), so the effect below runs per open.
  const resetMutation = create.reset;
  const [email, setEmail] = useState("");
  // setPasswordURL is present ONLY when the server has no mail configured;
  // then the admin has to relay the link by hand.
  const [done, setDone] = useState<{ email: string; setPasswordURL?: string } | null>(null);

  useEffect(() => {
    if (open) {
      setEmail("");
      setDone(null);
      resetMutation();
    }
  }, [open, resetMutation]);

  async function onSubmit() {
    try {
      const res = await create.mutateAsync({ email: email.trim() });
      setDone({ email: res.email, setPasswordURL: res.set_password_url });
    } catch {
      // Rendered from create.error below.
    }
  }

  return (
    <Modal
      open={open}
      title="Vytvořit účet vedoucímu"
      onClose={onClose}
      footer={
        done ? (
          <Button variant="primary" onClick={onClose}>
            Zavřít
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Zrušit</Button>
            <Button
              variant="primary"
              loading={create.isPending}
              disabled={!email.trim()}
              onClick={onSubmit}
            >
              Vytvořit účet
            </Button>
          </>
        )
      }
    >
      {done ? (
        <FormStack>
          <Alert variant="ok" title="Účet vytvořen">
            {leaderName} se nyní přihlásí jako {done.email} a může tým spravovat.
          </Alert>
          {done.setPasswordURL ? (
            // Only when the server cannot send mail. Somebody has to carry
            // the link then, so it is said out loud rather than hidden.
            <Alert variant="warn" title="Odesílání e-mailů není nastavené">
              Předejte vedoucímu tenhle odkaz; nastaví si jím heslo. Zobrazuje se jen teď.
              <br />
              <code style={{ wordBreak: "break-all" }}>{done.setPasswordURL}</code>
            </Alert>
          ) : (
            <Alert variant="info" title="Odkaz je na cestě">
              Vedoucímu jsme poslali e-mail s odkazem pro nastavení hesla. Platí sedm dní.
            </Alert>
          )}
        </FormStack>
      ) : (
        <FormStack>
          {create.error && <Alert variant="err">{apiErrorMessage(create.error, t)}</Alert>}
          <Input
            label="Přihlašovací e-mail"
            required
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Text variant="muted" as="p">
            Heslo tu nezadáváte. Vedoucí dostane e-mailem odkaz, kterým si ho nastaví sám.
          </Text>
        </FormStack>
      )}
    </Modal>
  );
}
